import { FC, useState, useEffect } from "react";
import { Spinner } from "@livingmap/hippo";
import { useTranslation } from "react-i18next";

import Workflow from "@components/Workflow/Workflow";
import PageTitle from "@components/PageTitle/PageTitle";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useGetMapsQuery } from "@redux/services/mms";
import {
  useGetProjectJobsQuery,
  useRunProjectJobMutation,
} from "@redux/services/workflows";
import { JobRun, Project } from "@redux/services/types";
import { setDefaultLanguage, setMapID } from "@redux/slices/applicationSlice";
import { getProjectNames } from "@utils";
import styles from "./WorkflowsView.module.scss";
import NavigationTopBar from "@components/NavigationTopBar/NavigationTopBar";
import ContentPanel from "@components/ContentPanel/ContentPanel";
import { useKeycloak } from "@react-keycloak/web";
import { ParsedToken } from "screens/FeaturesView/FeaturesView";

interface Props {
  onSignOut: () => void;
}

const WorkflowsView: FC<Props> = ({ onSignOut }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const { mapID, defaultLanguage } = useAppSelector(
    (state) => state.application,
  );
  const [pollingInterval, setPollingtInterval] = useState<number | undefined>(
    5000,
  );

  const { data, isLoading } = useGetMapsQuery();

  const { keycloak } = useKeycloak();
  const token = keycloak.tokenParsed as ParsedToken;

  const {
    currentData: jobsData,
    isError,
    error,
  } = useGetProjectJobsQuery(mapID, {
    pollingInterval,
    skip: !mapID,
  });

  useEffect(() => {
    if (isError && error && "status" in error && error.status === 404) {
      setPollingtInterval(undefined);
    }
  }, [isError, error]);

  useEffect(() => {
    setPollingtInterval(5000);
  }, [mapID]);

  const [runProject] = useRunProjectJobMutation();

  const projectNames = getProjectNames(data);

  const activeProjectName = projectNames?.find(
    (project) => project.id === mapID,
  );
  const activeProject = data?.data.find((project) => project.id === mapID);

  const handleProjectSelect = (project: Project) => {
    dispatch(setMapID(project.id));
    dispatch(setDefaultLanguage(project.defaultLanguage));
  };

  const getFinalRunData = (
    currentRun: JobRun | null,
    previousRun: JobRun,
  ): JobRun | null => {
    return {
      job_name:
        (currentRun && currentRun.job_name) ||
        (previousRun && previousRun.job_name),
      run_id:
        (currentRun && currentRun.run_id) ||
        (previousRun && previousRun.run_id),
      creation_time:
        (currentRun && currentRun.creation_time) ||
        (previousRun && previousRun.creation_time),
      status:
        (currentRun && currentRun.status) ||
        (previousRun && previousRun.status),
      start_time:
        (currentRun && currentRun.start_time) ||
        (previousRun && previousRun.start_time),
      end_time:
        (currentRun && currentRun.end_time) ||
        (previousRun && previousRun.end_time),
    };
  };

  const handleExecuteJob = (jobName: string) => {
    if (mapID) {
      runProject({ mapID, jobName });
    }
  };

  return isLoading ||
    !mapID ||
    !activeProject ||
    !activeProjectName ||
    !projectNames ? (
    <div className={styles.loaderContainer} data-qa="workflows-view-loading">
      <Spinner dataQA="loading-spinner" size={36} />
    </div>
  ) : (
    <div className={styles.container} data-qa="workflows-view">
      <NavigationTopBar
        dataQA="features-view-navigation-top-bar"
        activeLanguage={defaultLanguage}
        languages={activeProject?.languages.available}
        activeProject={activeProjectName}
        projects={projectNames}
        onProjectChange={handleProjectSelect}
        onLanguageChange={(lang) => {
          dispatch(setDefaultLanguage(lang.code));
          i18n.changeLanguage(lang.code);
        }}
        userProfile={token}
        userMenuItems={[
          {
            name: t("header.menu_items.sign_out"),
            icon: "logout",
            onClick: onSignOut,
            type: "primary",
          },
        ]}
      />
      <ContentPanel dataQA="workflows-view-content-panel">
        <PageTitle
          dataQA="workflows-view-page-title"
          heading={t("views.workflows.title")}
          subheading={t("views.workflows.description")}
        />
        <div className={styles.outletContainer}>
          {isError && error && "status" in error && error.status === 404 ? (
            <div className={styles.noWorkflowsContainer}>
              <h2 className={styles.noWorkflowsTitle}>
                {t("views.workflows.no_workflows.title")}
              </h2>
              <p className={styles.noWorkflowsDescription}>
                {t("views.workflows.no_workflows.description")}
              </p>
            </div>
          ) : !jobsData && !isError ? (
            <div className={styles.loaderContainer}>
              <Spinner dataQA="jobs-data-spinner" size={36} />
            </div>
          ) : (
            <div className={styles.workflowContainer}>
              <div className={styles.workflowWrapper}>
                {jobsData?.data.map((job) => {
                  const jobData = getFinalRunData(
                    job.current_run,
                    job.previous_run,
                  );

                  const isInProgress =
                    !!jobData?.status &&
                    jobData!.status !== "COMPLETED" &&
                    jobData!.status !== "FAILED";

                  return (
                    <Workflow
                      key={job.job_name}
                      dataQA={`${job.job_name}-workflow-item`}
                      description={job.ui.summary}
                      title={job.ui.title}
                      onClick={() => handleExecuteJob(job.job_name)}
                      inProgress={isInProgress}
                      executionTime={
                        jobData?.status === "PENDING" ||
                        jobData?.status === "RUNNING"
                          ? new Date().getTime() -
                            new Date(
                              job?.current_run?.creation_time || 0,
                            ).getTime()
                          : new Date(
                              job?.previous_run?.end_time || 0,
                            ).getTime() -
                            new Date(
                              job?.previous_run?.start_time || 0,
                            ).getTime()
                      }
                      lastRan={job?.previous_run?.end_time}
                      jobStatus={jobData?.status}
                      confirmation={job.ui.confirmation}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </ContentPanel>
    </div>
  );
};

export default WorkflowsView;
